import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, CircularProgress, Alert, Chip, InputAdornment,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Checkbox, FormControlLabel, Radio, RadioGroup,
  Autocomplete, Snackbar, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import CountrySelect from '../../components/CountriesDropBox/CountriesDropBox';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';
import {
  getVendorList, getVendorById, createVendor, updateVendor,
  deactivateVendor, activateVendor, getInternalUsers, apiRequest,
} from '../../api/api';

const TEMPLATE_OPTIONS = [
  { value: 'template_1', label: 'Template 1' },
  { value: 'template_2', label: 'Template 2' },
  { value: 'template_3', label: 'Template 3' },
  { value: 'template_4', label: 'Template 4' },
];

const SHIPPING_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'dhl', label: 'DHL (Express option at checkout)' },
  { value: 'fixed', label: 'Fixed Shipping' },
];

const TEMPLATE_EXAMPLES = {
  template_1: {
    headers: ['MFR', 'PART #', 'CC', 'INTERNAL PART #', 'LOCATION', 'SUB P/N', 'SUB 2 P/N', 'Description', 'Long Description', 'Category', 'Condition', 'QTY', 'Price'],
    example: ['LEN', '0000000AG562', '12', '0000000AG562-12-HU', 'US', '00AG560', '00AG562', 'LENOVO EMULEX VFA5.2 ML2 DUAL PORT 10GB ETHER', 'Warranty: 60 Days', 'Adapter - Network', 'New', '1', '119'],
    notes: [
      'CC and INTERNAL PART # columns are ignored during upload',
      'Long Description column will be saved to the specifications field',
      'If Price is "Call", the product will be marked as "Price on Demand"',
      'If Price is numeric, retail price will be calculated as Price + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{PART #}-{Condition}',
      'SUB P/N and SUB 2 P/N are optional fields',
    ],
  },
  template_2: {
    headers: ['Brand', 'Product Name', 'Product Description', 'Long Description', 'Part Number', 'Condition', 'Cost Price', 'Recommended Retail Price', 'Pack Weight', 'Pack Dimension', 'Product Location', 'No. of Stock/Units Available'],
    example: ['Dell', 'Hard Drive', '3.84TB 2.5" SAS SSD', 'Warranty: 60 Days', 'N85XX', 'New', '125.00', '150.00', 'Not Available', 'Not Available', 'UK', '50'],
    notes: [
      'Product Name column will be saved to the category field in the database',
      'Long Description column will be saved to the specifications field',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part Number}-{Condition}',
      'Recommended Retail Price is optional - if not provided, it will be calculated as Cost Price + 15%',
    ],
  },
  template_3: {
    headers: ['Vendor', 'Part No', 'Name', 'Long Description', 'Product Category', 'Qty', 'Unit Cost Price ($)'],
    example: ['LEN', '7DCE100EEA', 'ThinkSystem ST250 V3 6353P 32GB', 'Proc - Intel, Xeon E-2378, 8 Cores, 65W, 2.6GHz x1; Mem - 32GB TruDDR4...', 'Server', '25', '2884'],
    notes: [
      'Vendor column is ignored - vendor is set from the selection',
      'Long Description will be saved to the specifications field',
      'Brand is automatically set from vendor config, Condition to "New", and Location to "Netherlands"',
      'Retail price is automatically calculated as Unit Cost Price + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part No}-New',
    ],
  },
  template_4: {
    headers: ['Vendor', 'Part No', 'Name', 'Long Description', 'Catego', 'Qty', 'Unit Cost Price ($)'],
    example: ['LCR', '7D7QA031EA', 'ThinkSystem SR250 V2 E-2378 32GB', 'Proc - Intel, Xeon E-2378, 8 Cores, 65W, 2.6GHz x1; Mem - 32GB TruDDR4...', 'Server', '4', '1166.26'],
    notes: [
      'Vendor column is ignored - vendor is set from the selection',
      'Long Description column will be saved to the specifications field',
      'Retail price will be calculated as Unit Cost Price + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part No}-{Condition}',
    ],
  },
};

function emptyForm() {
  return {
    name: '', address_1: '', address_2: '', address_3: '',
    city: '', state: '', zip_code: '', country: '', phone: '', email: '',
    business_reg_no: '', vat_reg_no: '',
    catalog_template: '', shipping_method: '',
    contacts: [],
    cc_user_ids: [],
  };
}

const sectionTitle = { fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a', mb: 1, mt: 0.5 };
const fieldLabel = { fontSize: '0.75rem', color: '#666', fontWeight: 500, mb: 0.25 };

export default function VendorManagementPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVendorStatus, setEditVendorStatus] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [internalUsers, setInternalUsers] = useState([]);
  const [shippingRules, setShippingRules] = useState([]);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVendorList();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Failed to load vendors';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const filtered = vendors.filter(v => {
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return v.name.toLowerCase().includes(q) || (v.country || '').toLowerCase().includes(q);
    }
    return true;
  });

  const openCreate = async () => {
    setEditId(null);
    setEditVendorStatus(null);
    setForm(emptyForm());
    setDialogOpen(true);
    await loadHelpers();
  };

  const openEdit = async (id) => {
    try {
      const v = await getVendorById(id);
      setEditId(id);
      setEditVendorStatus(v.status || 'active');
      setForm({
        name: v.name || '',
        address_1: v.address_1 || '',
        address_2: v.address_2 || '',
        address_3: v.address_3 || '',
        city: v.city || '',
        state: v.state || '',
        zip_code: v.zip_code || '',
        country: v.country || '',
        phone: v.phone || '',
        email: v.email || '',
        business_reg_no: v.business_reg_no || '',
        vat_reg_no: v.vat_reg_no || '',
        catalog_template: v.catalog_template || '',
        shipping_method: v.shipping_method || '',
        contacts: v.contacts?.length ? v.contacts.map(c => ({
          name: c.name, email: c.email, phone: c.phone || '',
          job_role: c.job_role || '', is_notification_recipient: c.is_notification_recipient,
        })) : [],
        cc_user_ids: v.cc_users?.map(cu => cu.user_id) || [],
      });
      setDialogOpen(true);
      await loadHelpers();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Failed to load vendor details';
      showToast(msg, 'error');
    }
  };

  const loadHelpers = async () => {
    try {
      const [users, rules] = await Promise.all([
        getInternalUsers().catch(() => []),
        apiRequest('/shipping/rules', 'GET').catch(() => []),
      ]);
      setInternalUsers(Array.isArray(users) ? users : []);
      setShippingRules(Array.isArray(rules) ? rules : []);
    } catch (_) { /* helpers are non-critical */ }
  };

  const isValidEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleSave = async () => {
    const requiredProfile = [
      ['name', 'Vendor Name'], ['address_1', 'Address Line 1'],
      ['country', 'Country'], ['phone', 'Telephone'], ['email', 'Email'],
    ];
    for (const [key, label] of requiredProfile) {
      if (!form[key]?.trim()) { showToast(`${label} is required`, 'error'); return; }
    }

    // Validate vendor email format
    if (!isValidEmail(form.email)) {
      showToast('Please enter a valid vendor email address', 'error');
      return;
    }

    const validContacts = form.contacts.filter(c => c.name.trim() && c.email.trim());
    if (validContacts.length === 0) {
      showToast('At least one contact with name and email is required', 'error');
      return;
    }

    // Validate all contact emails
    for (const contact of validContacts) {
      if (!isValidEmail(contact.email)) {
        showToast(`Invalid email format for contact: ${contact.name || 'Unknown'}`, 'error');
        return;
      }
    }

    if (!validContacts.some(c => c.is_notification_recipient)) {
      showToast('A primary contact must be selected', 'error');
      return;
    }
    if (!form.catalog_template) {
      showToast('Catalog Upload Template is required', 'error');
      return;
    }
    if (!form.shipping_method) {
      showToast('Shipping Method is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address_1: form.address_1.trim() || null,
        address_2: form.address_2.trim() || null,
        address_3: form.address_3.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
        country: form.country.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        business_reg_no: form.business_reg_no.trim() || null,
        vat_reg_no: form.vat_reg_no.trim() || null,
        catalog_template: form.catalog_template || null,
        shipping_method: form.shipping_method || null,
        emails: [],
        contacts: validContacts,
        cc_user_ids: form.cc_user_ids,
      };
      if (editId) {
        await updateVendor(editId, payload);
        showToast('Vendor updated successfully');
      } else {
        await createVendor(payload);
        showToast('Vendor created successfully');
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Failed to save vendor';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!editId) return;
    try {
      if (editVendorStatus === 'active') {
        await deactivateVendor(editId);
        showToast('Vendor deactivated');
      } else {
        await activateVendor(editId);
        showToast('Vendor activated');
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Action failed';
      showToast(msg, 'error');
    }
  };

  const updateContact = (idx, field, val) => {
    const copy = [...form.contacts];
    copy[idx] = { ...copy[idx], [field]: val };
    setForm({ ...form, contacts: copy });
  };

  const addContact = () => {
    const isFirst = form.contacts.length === 0;
    setForm({
      ...form,
      contacts: [...form.contacts, {
        name: '', email: '', phone: '', job_role: '',
        is_notification_recipient: isFirst,
      }],
    });
  };

  const removeContact = (idx) => {
    const updated = form.contacts.filter((_, i) => i !== idx);
    if (updated.length > 0 && !updated.some(c => c.is_notification_recipient)) {
      updated[0].is_notification_recipient = true;
    }
    setForm({ ...form, contacts: updated });
  };

  const makePrimary = (idx) => {
    const copy = form.contacts.map((c, i) => ({
      ...c,
      is_notification_recipient: i === idx ? true : false,
    }));
    setForm({ ...form, contacts: copy });
  };

  const toggleNotify = (idx, checked) => {
    const copy = [...form.contacts];
    copy[idx] = { ...copy[idx], is_notification_recipient: checked };
    setForm({ ...form, contacts: copy });
  };

  const primaryIdx = form.contacts.findIndex(c => c.is_notification_recipient);

  const templateExample = TEMPLATE_EXAMPLES[form.catalog_template];

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const primaryContact = primaryIdx >= 0 ? form.contacts[primaryIdx] : null;

  const contactEmail = (v) => {
    const primary = (v.contacts || []).find(c => c.is_notification_recipient);
    if (primary) return primary.email;
    if (v.contacts?.length) return v.contacts[0].email;
    if (v.emails?.length) return v.emails.map(e => e.email).join(', ');
    return v.email || '-';
  };

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Vendor Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Vendor</Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small" placeholder="Search vendors..." value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ width: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contact Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Shipping</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#999' }}>
                        No vendors found
                      </TableCell>
                    </TableRow>
                  ) : filtered.map(v => (
                    <TableRow key={v.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{v.name}</TableCell>
                      <TableCell>{v.country || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>{contactEmail(v)}</TableCell>
                      <TableCell>{v.catalog_template ? v.catalog_template.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}</TableCell>
                      <TableCell>{v.shipping_method ? v.shipping_method.charAt(0).toUpperCase() + v.shipping_method.slice(1) : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={v.status === 'active' ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            fontWeight: 500, fontSize: '0.75rem',
                            backgroundColor: v.status === 'active' ? '#e8f5e9' : '#fbe9e7',
                            color: v.status === 'active' ? '#2e7d32' : '#bf360c',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(v.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Create / Edit Dialog ─────────────────────────── */}
          <Dialog
            open={dialogOpen}
            onClose={(_, reason) => { if (reason === 'backdropClick') return; setDialogOpen(false); }}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown={false}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{editId ? 'Edit Vendor' : 'Add Vendor'}</Typography>
              <IconButton onClick={() => setDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto', p: 3 }}>

              {/* ── Section: Vendor Profile ───────────────── */}
              <Typography sx={sectionTitle}>Vendor Profile</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 0.5 }}>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Vendor Name" required value={form.name} onChange={setField('name')} fullWidth size="small" />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <TextField label="Address Line 1" required value={form.address_1} onChange={setField('address_1')} fullWidth size="small" inputProps={{ maxLength: 45 }} helperText="Max 45 characters" />
                </Box>
                <TextField label="Address Line 2" value={form.address_2} onChange={setField('address_2')} fullWidth size="small" inputProps={{ maxLength: 45 }} helperText="Max 45 characters" />
                <TextField label="Address Line 3" value={form.address_3} onChange={setField('address_3')} fullWidth size="small" inputProps={{ maxLength: 45 }} helperText="Max 45 characters" />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 2, mb: 0.5 }}>
                <TextField label="City" value={form.city} onChange={setField('city')} fullWidth size="small" />
                <TextField label="State / Province" value={form.state} onChange={setField('state')} fullWidth size="small" />
                <TextField label="Zip / Postal Code" value={form.zip_code} onChange={setField('zip_code')} fullWidth size="small" />
                <Box>
                  <CountrySelect
                    value={form.country}
                    onChange={(value) => setForm({ ...form, country: value })}
                    sx={{ width: '100%' }}
                  />
                </Box>
                <TextField label="Telephone" required value={form.phone} onChange={setField('phone')} fullWidth size="small" />
                <TextField 
                  label="Email" 
                  required 
                  type="email"
                  value={form.email} 
                  onChange={setField('email')} 
                  fullWidth 
                  size="small"
                  error={form.email && !isValidEmail(form.email)}
                  helperText={form.email && !isValidEmail(form.email) ? 'Invalid email format' : ''}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                <TextField label="Business Reg. No. (optional)" value={form.business_reg_no} onChange={setField('business_reg_no')} fullWidth size="small" />
                <TextField label="VAT Reg. No. (optional)" value={form.vat_reg_no} onChange={setField('vat_reg_no')} fullWidth size="small" />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* ── Section: Contact Details ──────────────── */}
              <Typography sx={sectionTitle}>Contact Details <Typography component="span" color="error">*</Typography></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add contacts for this vendor. At least one contact is required. One contact must be designated as the <strong>Primary Contact</strong> who will always receive notification emails.
                Additional contacts can optionally be notified using the checkbox.
              </Typography>

              {form.contacts.length === 0 && (
                <Typography variant="body2" sx={{ mb: 1, color: '#999', fontStyle: 'italic' }}>
                  No contacts added yet. Click "Add Contact" to get started.
                </Typography>
              )}

              {form.contacts.map((c, idx) => {
                const isPrimary = (idx === primaryIdx);
                return (
                  <Paper
                    key={idx} variant="outlined"
                    sx={{
                      p: 2, mb: 2,
                      borderColor: isPrimary ? '#1565c0' : '#e0e0e0',
                      borderWidth: isPrimary ? 2 : 1,
                      backgroundColor: isPrimary ? '#e8f0fe' : '#fafafa',
                      borderRadius: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      {isPrimary ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1565c0' }}>
                            Primary Contact
                          </Typography>
                          <Chip label="Always notified" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', backgroundColor: '#bbdefb', color: '#0d47a1' }} />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="Make this the primary contact">
                            <IconButton size="small" onClick={() => makePrimary(idx)} sx={{ color: '#999' }}>
                              <StarBorderIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
                            Contact {idx + 1}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!c.is_notification_recipient}
                                onChange={ev => toggleNotify(idx, ev.target.checked)}
                              />
                            }
                            label={<Typography variant="caption" sx={{ color: '#666' }}>Notify</Typography>}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removeContact(idx)}
                        sx={{ color: '#bbb', '&:hover': { color: '#d32f2f' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
                      <TextField size="small" label="Contact Name" value={c.name} onChange={ev => updateContact(idx, 'name', ev.target.value)} fullWidth />
                      <TextField 
                        size="small" 
                        label="Contact Email" 
                        type="email"
                        value={c.email} 
                        onChange={ev => updateContact(idx, 'email', ev.target.value)} 
                        fullWidth
                        error={c.email && !isValidEmail(c.email)}
                        helperText={c.email && !isValidEmail(c.email) ? 'Invalid email' : ''}
                      />
                      <TextField size="small" label="Contact Phone" value={c.phone} onChange={ev => updateContact(idx, 'phone', ev.target.value)} fullWidth />
                      <TextField size="small" label="Job Role" value={c.job_role} onChange={ev => updateContact(idx, 'job_role', ev.target.value)} fullWidth />
                    </Box>
                  </Paper>
                );
              })}
              <Button size="small" onClick={addContact} startIcon={<AddIcon />} sx={{ mt: 0.5 }}>Add Contact</Button>

              <Divider sx={{ my: 3 }} />

              {/* ── Section: CC Users ─────────────────────── */}
              <Typography sx={sectionTitle}>APDI Internal Users to CC on Vendor Emails</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select APDI team members who should be CC'd on all emails sent to this vendor.
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={internalUsers}
                getOptionLabel={u => `${u.name} (${u.email})`}
                value={internalUsers.filter(u => form.cc_user_ids.includes(u.id))}
                onChange={(_, vals) => setForm({ ...form, cc_user_ids: vals.map(v => v.id) })}
                renderInput={(params) => <TextField {...params} label="Select Team Members" placeholder="Search..." />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
              />

              <Divider sx={{ my: 3 }} />

              {/* ── Section: Catalog Template ─────────────── */}
              <Typography sx={sectionTitle}>Catalog Upload Template <Typography component="span" color="error">*</Typography></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select the Excel template format used by this vendor for product catalog uploads.
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <RadioGroup
                  row
                  value={form.catalog_template}
                  onChange={e => setForm({ ...form, catalog_template: e.target.value })}
                >
                  {TEMPLATE_OPTIONS.map(t => (
                    <FormControlLabel key={t.value} value={t.value} control={<Radio size="small" />} label={t.label} />
                  ))}
                </RadioGroup>
              </FormControl>

              {templateExample && (
                <Alert icon={<InfoIcon />} severity="info" sx={{ mt: 1, mb: 1, backgroundColor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Example Format ({form.catalog_template.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())})
                  </Typography>
                  <Paper elevation={0} sx={{ mt: 1, mb: 1, overflow: 'auto', backgroundColor: '#fafafa' }}>
                    <Table size="small" sx={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fff3cd' }}>
                          {templateExample.headers.map((h, i) => (
                            <TableCell key={i} sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '6px 4px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          {templateExample.example.map((c, i) => (
                            <TableCell key={i} sx={{ fontSize: '0.7rem', padding: '6px 4px', border: '1px solid #ddd', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={c}>{c}</TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                  {templateExample.notes?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Notes:</Typography>
                      <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                        {templateExample.notes.map((n, i) => (
                          <li key={i}><Typography variant="caption" sx={{ color: '#555' }}>{n}</Typography></li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              {/* ── Section: Shipping ─────────────────────── */}
              <Typography sx={sectionTitle}>Shipping Method <Typography component="span" color="error">*</Typography></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose how shipping is calculated for orders from this vendor.
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <RadioGroup
                  row
                  value={form.shipping_method}
                  onChange={e => setForm({ ...form, shipping_method: e.target.value })}
                >
                  {SHIPPING_OPTIONS.map(s => (
                    <FormControlLabel key={s.value} value={s.value} control={<Radio size="small" />} label={s.label} />
                  ))}
                </RadioGroup>
              </FormControl>

              {form.shipping_method === 'fixed' && shippingRules.length > 0 && (
                <Alert icon={<InfoIcon />} severity="info" sx={{ mt: 1, backgroundColor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Fixed Shipping Matrix</Typography>
                  <Paper elevation={0} sx={{ overflow: 'auto', backgroundColor: '#fafafa' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fff3cd' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Category Group</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Base Charge</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Additional Charge</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Qty Threshold</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Manual Quote?</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shippingRules.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{r.category_group}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>${r.base_charge?.toFixed(2)}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>${r.additional_charge?.toFixed(2)}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{r.quantity_threshold}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{r.is_manual_quote ? 'Yes' : 'No'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Alert>
              )}

            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
              <Box>
                {editId && (
                  <Button
                    color={editVendorStatus === 'active' ? 'error' : 'success'}
                    onClick={handleToggleStatus}
                    variant="outlined"
                    size="small"
                  >
                    {editVendorStatus === 'active' ? 'Deactivate Vendor' : 'Activate Vendor'}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : (editId ? 'Update' : 'Create')}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={() => setToast(t => ({ ...t, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setToast(t => ({ ...t, open: false }))} severity={toast.severity} variant="filled">
              {toast.message}
            </Alert>
          </Snackbar>
        </ContentContainer>
      </PageContainer>
    </>
  );
}
